import React from 'react';
import { Coins, Plus, Gift } from 'lucide-react';
import { FitCoinBalance } from '../../services/fitcoin.service';

interface FitCoinsBalanceProps {
  balanceData: FitCoinBalance | null;
  onPurchase?: () => void;
  compact?: boolean;
  className?: string;
}

const FitCoinsBalance: React.FC<FitCoinsBalanceProps> = ({
  balanceData,
  onPurchase,
  compact = false,
  className = '',
}) => {
  const totalAvailable = balanceData?.totalAvailable ?? 0;
  const purchasedBalance = balanceData?.purchasedBalance ?? 0;
  const dailyAllowance = balanceData?.dailyAllowance ?? 0;
  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-full">
          <Coins size={16} className="text-amber-600" />
          <span className="text-sm font-bold text-amber-900">{totalAvailable}</span>
          {dailyAllowance > 0 && (
            <span className="text-xs text-amber-600" title="Daily allowance included">
              <Gift size={12} className="inline" />
            </span>
          )}
        </div>
        {onPurchase && (
          <button
            onClick={onPurchase}
            className="p-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-full transition-all hover:scale-105 shadow-sm"
            title="Buy Fit Coins"
          >
            <Plus size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl p-4 shadow-sm border border-gray-200 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-md">
            <Coins size={24} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Total Available</p>
            <p className="text-2xl font-bold text-gray-900">{totalAvailable}</p>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Purchased: {purchasedBalance}</span>
              {dailyAllowance > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Gift size={12} />
                  Daily: {dailyAllowance}
                </span>
              )}
            </div>
          </div>
        </div>
        {onPurchase && (
          <button
            onClick={onPurchase}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold rounded-xl transition-all hover:scale-105 shadow-md flex items-center gap-2"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Buy More</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FitCoinsBalance;
